import React, { useState } from "react";

import clsx from "clsx";
import { differenceBy } from "lodash";
import { components } from "react-select";
import SelectWithCreatableOptions from "react-select/creatable";

import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { isValidEmail } from "@shared/validators";

import { CheckboxOption } from "./checkbox-option";

interface InputTag {
  label: string;
  value: string;
}

interface Props {
  isLoading?: boolean;
  tags: InputTag[];
  initialOptions: InputTag[];
  onChange(val: InputTag[]): void;
  allowCreatingOptions?: boolean;
}

export function RecipientsSelector({
  isLoading = false,
  tags,
  initialOptions,
  onChange,
  allowCreatingOptions = false,
}: Props) {
  const [createdOptions, setCreatedOptions] = useState<string[]>([]);
  const prettyLogEventPage = usePrettyLogEventPage();

  const emailToInputTagParser = (email: string): InputTag => {
    const emailAsLowerCase = email.toLowerCase();
    return { label: emailAsLowerCase, value: emailAsLowerCase };
  };

  const isCreatedRecipient = (inputTag: InputTag) =>
    createdOptions.includes(inputTag.value.toLowerCase());

  const combinedOptions = !allowCreatingOptions
    ? initialOptions
    : [
        {
          options: createdOptions.map(emailToInputTagParser),
          label: "External users",
        },
        { options: initialOptions, label: "Internal users" },
      ];

  return (
    <SelectWithCreatableOptions
      menuPortalTarget={document.body}
      styles={{
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      components={{
        DropdownIndicator: (props) => (
          <components.DropdownIndicator {...props}>
            <i
              className={clsx(
                props.selectProps.menuIsOpen
                  ? "fa-chevron-up"
                  : "fa-chevron-down",
                "fa-solid text-[0.6rem] text-gray-600 px-2",
              )}
            />
          </components.DropdownIndicator>
        ),
        ClearIndicator: undefined,
        Input: CustomInput,
        MultiValueContainer: (props) => (
          <components.MultiValueContainer {...props}>
            <GenericTooltip
              noInfoCircle
              content={
                isCreatedRecipient(props.data)
                  ? "This is an external user"
                  : null
              }
              className="flex items-center justify-center"
            >
              {isCreatedRecipient(props.data) && (
                <i className="fa-regular fa-circle-exclamation text-sun+2" />
              )}
              {props.children}
            </GenericTooltip>
          </components.MultiValueContainer>
        ),
        MultiValueRemove: (props) => (
          <components.MultiValueRemove {...props}>
            <i className="fa-regular fa-xmark text-[0.8rem] text-gray-700 pt-0.5" />
          </components.MultiValueRemove>
        ),
        Option: CheckboxOption,
      }}
      classNames={{
        control: () => "max-h-[120px] overflow-y-auto shadow-none rounded-md",
        input: () => "text-xs font-light text-blue-500",
        multiValue: (props) => {
          return clsx(
            "bg-[#F5F8FE] rounded-2xl text-xs font-light px-1.5 items-center text-red-500",
            {
              "bg-sun-2 bg-opacity-20 border border-sun": isCreatedRecipient(
                props.data,
              ),
            },
          );
        },
        multiValueLabel: () => "text-blue-500",
        multiValueRemove: () => "bg-transparent text-black ml-0.5",
        indicatorSeparator: () => "hidden",
        menuList: () => "xl:max-h-[180px] max-h-[120px]",
        option: (props) =>
          props.isFocused
            ? "bg-[#F5F7FDB2] text-xs font-light text-black"
            : "bg-[#fff] text-xs font-light text-black",
      }}
      options={combinedOptions}
      isLoading={isLoading}
      isClearable
      isMulti
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      placeholder=""
      onChange={(selectedRecipients) => {
        prettyLogEventPage({
          domain: "DataExport",
          action: "onSelect",
          additionalActionInfo: "Data export recipient added",
          data: {
            newRecipients: selectedRecipients,
            pageContext: { recipients: initialOptions },
          },
        });

        const recipientsNotPartOfInitialOptions = differenceBy(
          selectedRecipients,
          initialOptions,
          "value",
        ).map((recipient) => recipient.value);

        setCreatedOptions(recipientsNotPartOfInitialOptions);

        onChange([...selectedRecipients]);
      }}
      createOptionPosition="first"
      isValidNewOption={(inputValue) =>
        allowCreatingOptions ? isValidEmail(inputValue) : false
      }
      getNewOptionData={emailToInputTagParser}
      noOptionsMessage={({ inputValue }) => {
        if (!allowCreatingOptions) {
          return "No options";
        }

        return isValidEmail(inputValue)
          ? `This email "${inputValue}" is already included`
          : "Almost there! You can only add complete email addresses";
      }}
      value={tags}
    />
  );
}

const CustomInput: typeof components.Input = (props) => (
  <components.Input {...props} type="email" inputMode="email" />
);
